/**
 * Traduz respostas de erro do BFF para mensagens amigáveis em PT-BR.
 *
 * O cliente HTTP do `@dt-pm/core` normaliza tudo em `ApiError { status, code, message }`,
 * com códigos estáveis tanto para erros do backend (ex.: `PROJECT_WORK_PARTICIPANT_NOT_REFERENCEABLE`)
 * quanto para falhas de rede/HTTP (ex.: `NETWORK_ERROR`, `FORBIDDEN`).
 *
 * O toast consome apenas `{ title, description }` — nunca `err.message` cru.
 */

import { isApiError } from '@dt-pm/core';

export interface FormattedApiError {
  title: string;
  description?: string;
}

const FRIENDLY_BY_CODE: Record<string, FormattedApiError> = {
  PROJECT_LIFECYCLE_TRANSITION_FORBIDDEN: {
    title: 'Sem permissão para alterar o status do projeto',
    description: 'Peça a um administrador para retomar ou alterar o status do projeto.',
  },
  // Códigos do backend (snake_case, vêm do BFF).
  PROJECT_NOT_ACTIVE: {
    title: 'Registro de horas não disponível',
    description:
      'O projeto está pausado e não aceita novos registros de hora. Retome o projeto para continuar.',
  },
  PROJECT_WORK_PARTICIPANT_NOT_REFERENCEABLE: {
    title: 'Você não participa deste projeto',
    description:
      'Só é possível agir em projetos nos quais você é membro. Peça ao gestor para te incluir como participante.',
  },
  PROJECT_PARTICIPANT_NOT_REFERENCEABLE: {
    title: 'Participante não pertence ao projeto',
    description: 'Adicione a pessoa como participante do projeto antes de atribuí-la a uma atividade.',
  },
  PROJECT_WORK_PARTICIPANT_NOT_FOUND: {
    title: 'Participante do projeto não encontrado',
    description: 'Atualize a página e tente novamente.',
  },
  TRANSACTION_SCOPE_MISSING: {
    title: 'Operação interrompida',
    description:
      'Não conseguimos salvar sua alteração porque outra ação estava em andamento. Tente novamente em alguns segundos.',
  },
  RATE_LIMITED: {
    title: 'Muitas requisições em sequência',
    description: 'Aguarde alguns instantes antes de tentar novamente.',
  },
  // Códigos normalizados pelo cliente HTTP (packages/core/src/http/errors.ts).
  NETWORK_ERROR: {
    title: 'Sem conexão com o servidor',
    description: 'Verifique sua internet e tente novamente.',
  },
  TIMEOUT: {
    title: 'A requisição demorou demais',
    description: 'Tente novamente em alguns instantes.',
  },
  UNAUTHORIZED: {
    title: 'Sua sessão expirou',
    description: 'Faça login novamente para continuar.',
  },
  FORBIDDEN: {
    title: 'Sem permissão para essa ação',
    description: 'Sua conta não tem autorização para executar essa operação.',
  },
  NOT_FOUND: {
    title: 'Item não encontrado',
    description:
      'Ele pode ter sido removido por outra pessoa. Atualize a página para ver a versão mais recente.',
  },
  ATTACHMENT_NOT_FOUND: {
    title: 'Arquivo não encontrado',
    description: 'Ele pode ter sido removido. Atualize a lista e tente novamente.',
  },
  ATTACHMENT_NOT_AVAILABLE: {
    title: 'Arquivo indisponível',
    description: 'Não há um link válido para baixar este arquivo.',
  },
  ATTACHMENT_MODIFICATION_FORBIDDEN: {
    title: 'Sem permissão para excluir',
    description: 'Só quem enviou o arquivo ou um administrador pode excluí-lo.',
  },
  CONFLICT: {
    title: 'Conflito de versão',
    description: 'Outra pessoa alterou esse item. Recarregue a página para ver as mudanças mais recentes.',
  },
  // Códigos específicos do time-tracking — o backend retorna 409 quando o intervalo
  // enviado cruza um registro já existente, ou códigos dedicados como
  // `TIME_ENTRY_OVERLAP` / `TIME_ENTRY_CONFLICT`.
  TIME_ENTRY_CONFLICT: {
    title: 'Horário já registrado',
    description:
      'Esse intervalo de horário já foi preenchido por outro apontamento. Ajuste a hora de início/fim ou remova o registro existente para liberar esse espaço.',
  },
  TIME_ENTRY_OVERLAP: {
    title: 'Horário já registrado',
    description:
      'Esse intervalo de horário já foi preenchido por outro apontamento. Ajuste a hora de início/fim ou remova o registro existente para liberar esse espaço.',
  },
  VALIDATION_ERROR: {
    title: 'Verifique os dados informados',
    description: 'Alguns campos precisam de ajuste antes de salvar.',
  },
  SERVER_ERROR: {
    title: 'Erro inesperado no servidor',
    description: 'Tente novamente em alguns instantes.',
  },
};

const DEFAULT_FALLBACK: FormattedApiError = {
  title: 'Algo deu errado',
  description: 'Tente novamente em alguns instantes. Se o problema persistir, recarregue a página.',
};

const FALLBACK_BY_OPERATION: Record<string, FormattedApiError> = {
  save: {
    title: 'Não foi possível salvar',
    description: 'Tente novamente em alguns instantes.',
  },
  update: {
    title: 'Não foi possível atualizar',
    description: 'Tente novamente em alguns instantes.',
  },
  create: {
    title: 'Não foi possível criar o item',
    description: 'Tente novamente em alguns instantes.',
  },
  delete: {
    title: 'Não foi possível remover',
    description: 'Tente novamente em alguns instantes.',
  },
  move: {
    title: 'Não foi possível mover o item',
    description: 'Tente novamente em alguns instantes.',
  },
  duplicate: {
    title: 'Não foi possível duplicar',
    description: 'Tente novamente em alguns instantes.',
  },
  default: DEFAULT_FALLBACK,
};

export type ApiOperation =
  | 'save'
  | 'update'
  | 'create'
  | 'delete'
  | 'move'
  | 'duplicate'
  | 'time-entry-create'
  | 'time-entry-update'
  | 'time-entry-delete'
  | 'default';

const NOT_ON_PROJECT_MESSAGE =
  'Você não visualiza as tarefas deste projeto porque não está adicionado a ele.';

const NOT_ON_PROJECT_CODES = new Set([
  'PROJECT_PARTICIPANT_NOT_REFERENCEABLE',
  'PROJECT_WORK_PARTICIPANT_NOT_REFERENCEABLE',
]);

/** Texto do quadro quando a leitura das tarefas falha. */
export function formatBoardLoadError(err: unknown): string {
  if (isApiError(err) && NOT_ON_PROJECT_CODES.has(err.code)) return NOT_ON_PROJECT_MESSAGE;
  if (
    err instanceof Error &&
    /not referenceable|does not belong to this project/i.test(err.message)
  ) {
    return NOT_ON_PROJECT_MESSAGE;
  }

  const formatted = formatApiError(err);
  return formatted.description ? `${formatted.title} ${formatted.description}` : formatted.title;
}

/**
 * Converte um erro genérico em algo apresentável.
 * @param err  erro capturado
 * @param op   operação que falhou — define o texto padrão quando o código é desconhecido
 */
export function formatApiError(err: unknown, op: ApiOperation = 'default'): FormattedApiError {
  if (isApiError(err)) {
    const known = FRIENDLY_BY_CODE[err.code];
    if (known) return known;
    // 409 sem código específico do time-tracking: o backend sinalizou apenas
    // o status. Em criação/edição de registro de horas, isso quase sempre é
    // sobreposição de horário — usamos a mensagem amigável específica em vez
    // do "Conflito de versão" genérico.
    if (err.isConflict && (op === 'time-entry-create' || op === 'time-entry-update')) {
      return FRIENDLY_BY_CODE.TIME_ENTRY_CONFLICT!;
    }
  }
  return FALLBACK_BY_OPERATION[op] ?? DEFAULT_FALLBACK;
}
